import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  AddRounded,
  VisibilityRounded,
  EditRounded,
  CampaignRounded,
  CloseRounded,
} from '@mui/icons-material';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import {
  CreateAnnouncementDialog,
  type AnnouncementInitialData,
} from './components/CreateAnnouncementDialog';
import type { AudienceTarget } from '../../lib/api/announcement.api';
import { formatDate } from '../../lib/i18n-format';

interface AnnouncementDoc {
  id: string;
  title: string;
  content: string;
  status: 'draft' | 'published' | 'archived';
  audience: AudienceTarget;
  authorName?: string;
  createdAt: string;
  publishedAt: string | null;
  visibility: { visibleFrom: string | null; visibleUntil: string | null };
}

const STATUS_CHIP: Record<
  string,
  { label: string; color: 'default' | 'warning' | 'success' | 'error' | 'info' }
> = {
  draft: { label: 'Borrador', color: 'default' },
  published: { label: 'Publicado', color: 'success' },
  archived: { label: 'Archivado', color: 'warning' },
};

function dateLabel(val: string | null): string {
  if (!val) return '—';
  return formatDate(val, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Convert bare URLs in HTML content into clickable <a> links.
 * Splits on existing <a>…</a> blocks so already-linked text is never double-wrapped.
 */
function linkifyHtml(html: string): string {
  const ANCHOR_RE = /(<a\s[^>]*>[\s\S]*?<\/a>)/gi;
  const URL_RE = /(https?:\/\/[^\s<>"']+)/g;
  return html
    .split(ANCHOR_RE)
    .map((segment, i) =>
      i % 2 === 1
        ? segment
        : segment.replace(
            URL_RE,
            (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`,
          ),
    )
    .join('');
}

// ── Preview Dialog ────────────────────────────────────────────────────────────

function AnnouncementPreviewDialog({
  announcement,
  onClose,
}: {
  announcement: AnnouncementDoc | null;
  onClose: () => void;
}) {
  const open = !!announcement;
  if (!announcement) return null;

  const chipInfo = STATUS_CHIP[announcement.status] ?? {
    label: announcement.status,
    color: 'default' as const,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box flex={1} pr={2}>
            <Stack direction="row" spacing={1.5} alignItems="center" mb={0.5}>
              <Chip label={chipInfo.label} color={chipInfo.color} size="small" variant="outlined" />
              <Typography variant="caption" color="text.secondary">
                {announcement.authorName ? `Por: ${announcement.authorName}` : 'Administración'}
              </Typography>
            </Stack>
            <Typography variant="h6" fontWeight={700} lineHeight={1.3}>
              {announcement.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Público: {announcement.audience?.label || 'Todo el seminario'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ flexShrink: 0 }}>
            <CloseRounded />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider sx={{ mt: 1.5 }} />

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        <Box
          sx={{
            // Reset Quill defaults so text reads naturally without giant gaps
            '& p': {
              m: 0,
              mb: 0.5,
              lineHeight: 1.7,
              fontSize: '0.92rem',
            },
            // Empty <p><br></p> (Quill blank lines) → half-height gap only
            '& p:has(> br:only-child)': {
              mb: 0,
              lineHeight: 0.6,
            },
            '& h2, & h3': { mt: 2, mb: 0.5, fontWeight: 700 },
            '& ul, & ol': { pl: 3, my: 0.5 },
            '& li': { mb: 0.25, lineHeight: 1.7 },
            '& a': {
              color: 'primary.main',
              textDecoration: 'underline',
              wordBreak: 'break-all',
            },
            '& strong': { fontWeight: 700 },
          }}
          dangerouslySetInnerHTML={{ __html: linkifyHtml(announcement.content) }}
        />
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {!announcement.visibility?.visibleFrom && !announcement.visibility?.visibleUntil
            ? 'Siempre visible'
            : `${dateLabel(announcement.visibility?.visibleFrom ?? null)} → ${dateLabel(announcement.visibility?.visibleUntil ?? null)}`}
        </Typography>
        <Button onClick={onClose} color="inherit" size="small">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function AnnouncementsAdminPage() {
  const { t } = useTranslation('sis');
  const { currentTenantId: tenantId } = useTenantStore();
  const [announcements, setAnnouncements] = useState<AnnouncementDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AnnouncementInitialData | undefined>(undefined);
  const [previewTarget, setPreviewTarget] = useState<AnnouncementDoc | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    const unsub = onSnapshot(
      query(
        collection(db, `tenants/${tenantId}/announcements`),
        orderBy('createdAt', 'desc'),
        limit(50),
      ),
      (snap) => {
        const results = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<AnnouncementDoc, 'id'>),
        })) as AnnouncementDoc[];
        setAnnouncements(results);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading announcements:', err);
        setLoading(false);
      },
    );
    return unsub;
  }, [tenantId]);

  const handleOpenEdit = (announcement: AnnouncementDoc) => {
    const data: AnnouncementInitialData = {
      id: announcement.id,
      title: announcement.title,
      content: announcement.content,
      status: announcement.status === 'archived' ? 'published' : announcement.status,
      audience: announcement.audience,
      visibility: announcement.visibility ?? { visibleFrom: null, visibleUntil: null },
    };
    setEditTarget(data);
    setCreateOpen(true);
  };

  const handleCloseDialog = () => {
    setCreateOpen(false);
    setEditTarget(undefined);
  };

  if (!tenantId) return null;

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Header ──────────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: 'secondary.main', width: 44, height: 44 }}>
            <CampaignRounded />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {t('announcementsAdmin.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('announcementsAdmin.subtitle')}
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddRounded />}
          onClick={() => {
            setEditTarget(undefined);
            setCreateOpen(true);
          }}
          sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
        >
          Nuevo Anuncio
        </Button>
      </Stack>

      {/* ── Content ────────────────────────────────────────────────── */}
      {loading ? (
        <Stack alignItems="center" py={8}>
          <CircularProgress />
        </Stack>
      ) : announcements.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3,
            p: 6,
            textAlign: 'center',
            borderStyle: 'dashed',
            borderColor: 'divider',
          }}
        >
          <CampaignRounded sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
          <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
            No hay anuncios publicados
          </Typography>
          <Typography variant="body2" color="text.disabled" mb={2.5}>
            Crea un nuevo anuncio para comunicarte con la comunidad
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddRounded />}
            onClick={() => {
              setEditTarget(undefined);
              setCreateOpen(true);
            }}
          >
            Crear anuncio
          </Button>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      color: 'text.secondary',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <TableCell>TÍTULO</TableCell>
                  <TableCell>PÚBLICO</TableCell>
                  <TableCell>ESTADO</TableCell>
                  <TableCell>PUBLICADO EL</TableCell>
                  <TableCell>VISIBILIDAD</TableCell>
                  <TableCell align="right">ACCIONES</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {announcements.map((announcement) => {
                  const chipInfo = STATUS_CHIP[announcement.status] ?? {
                    label: announcement.status,
                    color: 'default' as const,
                  };
                  return (
                    <TableRow key={announcement.id} hover>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight={600} color="text.primary">
                            {announcement.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Por: {announcement.authorName || 'Administración'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          sx={{ maxWidth: 180 }}
                        >
                          {announcement.audience?.label || 'Todo el seminario'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={chipInfo.label}
                          color={chipInfo.color}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        {dateLabel(announcement.publishedAt)}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        {!announcement.visibility?.visibleFrom &&
                        !announcement.visibility?.visibleUntil
                          ? 'Siempre visible'
                          : `${dateLabel(announcement.visibility?.visibleFrom ?? null)} → ${dateLabel(announcement.visibility?.visibleUntil ?? null)}`}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Vista previa">
                            <IconButton size="small" onClick={() => setPreviewTarget(announcement)}>
                              <VisibilityRounded fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => handleOpenEdit(announcement)}>
                              <EditRounded fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <CreateAnnouncementDialog
        open={createOpen}
        onClose={handleCloseDialog}
        tenantId={tenantId}
        {...(editTarget ? { initialData: editTarget } : {})}
      />

      <AnnouncementPreviewDialog
        announcement={previewTarget}
        onClose={() => setPreviewTarget(null)}
      />
    </Box>
  );
}
